import { maskPii } from './console-capture';

/**
 * NetworkCapture — Sikertelen API hívások rögzítése.
 * A fetch API-t monkey-patch-eli (az rPI ERP fetch-alapú ApiClient-et használ, nem Axios-t).
 * Ring buffer, max 50 entry.
 */

export interface FailedApiEntry {
  url: string;
  method: string;
  status: number;
  message?: string;
  timestamp: number;
}

const MAX_ENTRIES = 50;

/**
 * T-71-21 (a), 🛡️ CR `#2164` F1+F3: a jegy (`HelpdeskDrawer.tsx:258`) a hibás hívás URL-jét
 * csatolja, és a jegy külső rendszerbe (Google Sheets) megy. A PII-t hordozó GET-ek a QUERY
 * STRINGBEN viszik az adatot (`partners.ts:384` `taxNumber` · `use-pos-transaction.ts:240-241`
 * `search` + `taxNumber`), gyakran URL-KÓDOLVA (`%40`, `%2B36…`) — amit a `maskPii` nem fog.
 * Ezért a query stringet és a fragmentet NEM maszkoljuk, hanem ELDOBJUK; csak a jelenlétük
 * marad (`?…`), a hibakereséshez a végpont és a státusz elég. Az útvonal maszkon megy át
 * (egy `/by-email/<cím>` alakú szegmens ellen).
 */
export function redactUrl(url: string): string {
  const cut = url.search(/[?#]/);
  const path = cut === -1 ? url : url.slice(0, cut);
  return maskPii(path) + (cut === -1 ? '' : '?…');
}

export class NetworkCaptureService {
  private entries: FailedApiEntry[] = [];
  private initialized = false;

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    const origFetch = window.fetch.bind(window);

    window.fetch = async (
      input: Parameters<typeof fetch>[0],
      init?: Parameters<typeof fetch>[1]
    ): Promise<Response> => {
      const response = await origFetch(input, init);

      // Csak 4xx/5xx hibákat rögzítjük, és csak /api/ hívásokat
      if (response.status >= 400) {
        const url =
          typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
        if (url.includes('/api/')) {
          this.push({
            url: url.replace(window.location.origin, ''),
            method: (init?.method || 'GET').toUpperCase(),
            status: response.status,
            timestamp: Date.now(),
          });
        }
      }

      return response;
    };
  }

  private push(entry: FailedApiEntry): void {
    // Az EGYETLEN bemenet: a `fetch`-patch ide ír — a redakció itt nem kerülhető meg.
    this.entries.push({
      ...entry,
      url: redactUrl(entry.url),
      ...(entry.message !== undefined && { message: maskPii(entry.message) }),
    });
    if (this.entries.length > MAX_ENTRIES) {
      this.entries.shift();
    }
  }

  /** Utolsó N entry (ticket csatoláshoz) */
  getRecent(limit = 10): FailedApiEntry[] {
    return this.entries.slice(-limit);
  }

  getCount(): number {
    return this.entries.length;
  }

  clear(): void {
    this.entries = [];
  }
}

export const networkCapture = new NetworkCaptureService();
